// Phase APP-4 · MHM "+ Suggest Micro Hub" modal.
// 7 fields per APP-1 MicroHubSuggestionPayload. Submit disabled until valid.
// POST /api/hub/micro-hubs/suggest
import { useEffect, useState } from "react";
import { X, Loader2, Building2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../../services/api";

const ORANGE = "#FF6B00";

export default function SuggestMicroHubModal({ open, onClose, onDone }) {
  const [form, setForm] = useState({
    name: "", city: "", pincode: "", address: "",
    expected_capacity: "", assigned_lead_user_id: "", remarks: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ name: "", city: "", pincode: "", address: "",
                  expected_capacity: "", assigned_lead_user_id: "", remarks: "" });
      setBusy(false); setErr("");
    }
  }, [open]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const cap = parseInt(form.expected_capacity || "0", 10);
  const valid = form.name.trim().length >= 3
                && form.name.trim().length <= 120
                && form.city.trim().length > 0
                && /^\d{6}$/.test(form.pincode.trim())
                && form.address.trim().length > 0
                && cap >= 1 && cap <= 10000
                && (form.remarks || "").length <= 500;

  const submit = async () => {
    if (!valid) return;
    setBusy(true); setErr("");
    try {
      const body = {
        name: form.name.trim(),
        city: form.city.trim(),
        pincode: form.pincode.trim(),
        address: form.address.trim(),
        expected_capacity: cap,
        remarks: form.remarks.trim(),
      };
      if (form.assigned_lead_user_id.trim()) {
        body.assigned_lead_user_id = form.assigned_lead_user_id.trim();
      }
      const r = await api.post("/hub/micro-hubs/suggest", body);
      toast.success(`Suggestion submitted · ${r.data.request_id}`);
      onDone?.(r.data?.request_id);
      onClose?.();
    } catch (e2) {
      const detail = e2?.response?.data?.detail || e2?.message || "Failed";
      setErr(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setBusy(false);
    }
  };

  const onBackdrop = (e) => { if (e.target === e.currentTarget && !busy) onClose?.(); };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-2"
         onClick={onBackdrop}
         data-testid="suggest-micro-hub-modal"
         role="dialog" aria-modal="true">
      <div className="bg-white w-full sm:max-w-lg rounded-2xl shadow-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <Building2 className="h-4 w-4" style={{ color: ORANGE }}/>
          <h3 className="text-base font-semibold flex-1">Suggest a Micro Hub</h3>
          <button onClick={onClose} aria-label="Close"
                  data-testid="suggest-modal-close"
                  className="p-1.5 rounded-full hover:bg-slate-100" disabled={busy}>
            <X className="h-4 w-4"/>
          </button>
        </div>
        <div className="px-4 py-3 space-y-3 max-h-[70vh] overflow-y-auto">
          {err && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-700 inline-flex items-start gap-1.5"
                 data-testid="suggest-modal-error">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0"/> {err}
            </div>
          )}
          <Field label="Name *" hint="3–120 chars">
            <input value={form.name} onChange={(e) => set("name", e.target.value)}
                   data-testid="suggest-name"
                   className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="City *">
              <input value={form.city} onChange={(e) => set("city", e.target.value)}
                     data-testid="suggest-city"
                     className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
            </Field>
            <Field label="Pincode *" hint="6 digits">
              <input value={form.pincode} onChange={(e) => set("pincode", e.target.value)}
                     maxLength={6} data-testid="suggest-pincode"
                     className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono"/>
            </Field>
          </div>
          <Field label="Address *">
            <textarea value={form.address} onChange={(e) => set("address", e.target.value)}
                      rows={2} data-testid="suggest-address"
                      className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm resize-none"/>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Expected capacity *" hint="1–10000">
              <input type="number" min={1} max={10000}
                     value={form.expected_capacity}
                     onChange={(e) => set("expected_capacity", e.target.value)}
                     data-testid="suggest-capacity"
                     className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
            </Field>
            <Field label="Assigned lead (optional)" hint="user id">
              <input value={form.assigned_lead_user_id}
                     onChange={(e) => set("assigned_lead_user_id", e.target.value)}
                     placeholder="leave blank"
                     data-testid="suggest-lead"
                     className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono"/>
            </Field>
          </div>
          <Field label="Remarks" hint={`${(form.remarks || "").length}/500`}>
            <textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)}
                      rows={2} data-testid="suggest-remarks"
                      maxLength={500}
                      className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm resize-none"/>
          </Field>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  data-testid="suggest-cancel"
                  className="text-sm px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={submit}
                  disabled={!valid || busy}
                  data-testid="suggest-submit"
                  data-can-submit={valid && !busy ? "true" : "false"}
                  className="text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  style={{ backgroundColor: ORANGE }}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin"/>}
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-baseline justify-between">
        <span>{label}</span>
        {hint && <span className="normal-case text-slate-400">{hint}</span>}
      </label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
